/**
 * Typed client for the career curriculum (`/api/careers/:id/curriculum`, public, with the learner's
 * state when signed in), the admin track checklists (`/api/admin/careers/...`) and the learner's
 * external evidence (`/api/portfolio/external`). Shapes mirror
 * `packages/server/src/services/curriculum.ts` and `services/portfolio.ts`.
 *
 * The curriculum is a read model: nothing here writes mastery. External evidence is the learner's
 * own account of work outside WorkSim, labeled self-reported and never scored.
 */
import { request } from './client';
import type {
  CareerCurriculum,
  ChecklistItem,
  ChecklistStatus,
  CurriculumItemRef,
  CurriculumSkill,
  CurriculumStep,
  ExternalEvidence,
  ExternalEvidenceOrigin,
  PortfolioItem,
  TrackChecklist,
} from '@worksim/engine';

export type { CareerCurriculum, ChecklistItem, ChecklistStatus, CurriculumItemRef, CurriculumSkill, CurriculumStep, ExternalEvidence, ExternalEvidenceOrigin, TrackChecklist };

const enc = encodeURIComponent;

/** Per-skill item lists the compact view reduces to counts. */
export type SkillListKey = 'practice' | 'labs' | 'environments' | 'workplace' | 'projects' | 'assessments' | 'interview' | 'certifications' | 'resources' | 'cheatSheets';

/** A curriculum skill as the compact view serves it: scalar fields plus counts. */
export type CompactCurriculumSkill = Omit<CurriculumSkill, SkillListKey> & { counts: Record<SkillListKey, number> };

/** `GET /careers/:id/curriculum?view=compact`: every step with its top items, skills with counts. */
export type CompactCareerCurriculum = Omit<CareerCurriculum, 'skillMap'> & { skillMap: { tier: CareerCurriculum['skillMap'][number]['tier']; skills: CompactCurriculumSkill[] }[] };

/** One row of the admin "All tracks" table. */
export interface ChecklistSummary {
  careerId: string;
  careerName: string;
  status: string;
  complete: number;
  total: number;
  gaps: number;
}

/** What the learner sends for one piece of outside evidence. The server checks it again. */
export interface ExternalEvidenceInput {
  careerId: string;
  origin: ExternalEvidenceOrigin;
  title: string;
  summary: string;
  skillIds?: string[];
  link?: string;
  completedAt: string;
}

export const curriculum = {
  /** The compact curriculum (the page never needs every skill's full item lists). */
  career: (careerId: string) => request<{ curriculum: CompactCareerCurriculum }>('GET', `/careers/${enc(careerId)}/curriculum`, undefined, { view: 'compact' }),
  checklist: (careerId: string) => request<{ checklist: TrackChecklist }>('GET', `/admin/careers/${enc(careerId)}/checklist`),
  checklists: () => request<{ careers: ChecklistSummary[] }>('GET', '/admin/careers/checklists'),
};

export const externalEvidence = {
  list: () => request<{ items: ExternalEvidence[]; notice: string }>('GET', '/portfolio/external'),
  add: (input: ExternalEvidenceInput) => request<{ item: PortfolioItem; evidence: ExternalEvidence; notice: string }>('POST', '/portfolio/external', input),
  remove: (id: string) => request<{ deleted: true }>('DELETE', `/portfolio/external/${enc(id)}`),
};
